import type { TableColumnType as ColumnType } from 'antd';
import moment from 'moment-timezone';
import React, { useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import EnterpriseIndustryText from 'components/atoms/EnterpriseIndustryText';
import EnterpriseStatusText from 'components/atoms/EnterpriseStatusText';
import DevicesLink from 'components/composites/DevicesLink';
import EnterpriseLink from 'components/composites/EnterpriseLink';
import BackOfficeTable, {
  Props as BackOfficeTableProps
} from 'components/tables/BackOfficeTable';
import { SEARCH_PARAMS } from 'constants/search-params';
import { DATE_TIME_FORMAT } from 'constants/time';
import { useBackOfficeEnterprises } from 'store/modules/backOfficeEnterprises/hooks';
import { selectBackOfficeEnterprisesTotal } from 'store/modules/backOfficeEnterprises/selectors';
import Enterprise, { EnterpriseStatus, Industry } from 'types/models/enterprise';

const loadAction = 'LOAD_BACK_OFFICE_ENTERPRISES';

const columns: ColumnType<Enterprise>[] = [
  {
    title: 'Name',
    dataIndex: 'name',
    width: 200,
    ellipsis: true,
    fixed: 'left',
    sorter: true
  },
  {
    title: 'ID',
    dataIndex: 'id',
    width: 65,
    ellipsis: true,
    sorter: true
  },
  {
    title: 'Web Domain',
    key: 'webDomain',
    width: 120,
    ellipsis: true,
    render: (_, enterprise: Enterprise) => (
      <EnterpriseLink enterprise={enterprise} />
    )
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (enterpriseStatus: EnterpriseStatus) => (
      <EnterpriseStatusText enterpriseStatus={enterpriseStatus} />
    ),
    width: 150,
    ellipsis: true
  },
  {
    title: 'Devices',
    key: 'devices',
    dataIndex: 'id',
    width: 100,
    ellipsis: true,
    render: (enterpriseId: number) => (
      <DevicesLink enterpriseId={enterpriseId} />
    )
  },
  {
    title: 'State',
    key: 'state',
    dataIndex: ['billingAddress', 'state', 'abbreviation'],
    width: 150,
    ellipsis: true
  },
  {
    title: 'Country',
    key: 'country',
    dataIndex: ['billingAddress', 'country'],
    width: 150,
    ellipsis: true,
    render: (country: string) => <div>{country}</div>
  },
  {
    title: 'Created At',
    key: 'createdAt',
    dataIndex: 'createdAt',
    width: 250,
    ellipsis: false,
    render: (createdAt: number) => (
      <div>{moment(createdAt).format(DATE_TIME_FORMAT.DEFAULT)}</div>
    )
  },
  {
    title: 'Industry',
    dataIndex: 'industry',
    render: (industry: Industry) => (
      <EnterpriseIndustryText industry={industry} />
    ),
    width: 200,
    ellipsis: true
  }
];

function BackOfficeEnterprisesTable({
  onPaginationChange
}: Pick<BackOfficeTableProps<Enterprise>, 'onPaginationChange'>): JSX.Element {
  const backOfficeEnterprisesTotal = useSelector(
    selectBackOfficeEnterprisesTotal,
    shallowEqual
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const enterpriseId = Number(searchParams.get(SEARCH_PARAMS.BACK_OFFICE.SELECTED_ENTERPRISE)) || undefined;

  const enterprises = useBackOfficeEnterprises();

  const onRowClick = useCallback((id: number) => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.DRAWER.key);
      newSearchParams.set(SEARCH_PARAMS.BACK_OFFICE.SELECTED_ENTERPRISE, String(id));
      return newSearchParams;
    });
  }, [setSearchParams]);

  return (
    <BackOfficeTable<Enterprise>
      columns={columns}
      data={enterprises}
      total={backOfficeEnterprisesTotal}
      loadActionType={loadAction}
      onRowClick={onRowClick}
      onPaginationChange={onPaginationChange}
      emptyText="No enterprises found"
      selectedRowId={enterpriseId}
    />
  );
}

export default BackOfficeEnterprisesTable;
